import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MoreVertical,
  Plus,
  Search,
  Pencil,
  Percent,
  ShieldCheck,
  Settings2,
  BadgeDollarSign,
  HandCoins,
  X,
  Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { apiFetch } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SettingsMenu } from "@/components/Holizontal/SettingsMenu";
import { helper } from "@/lib/helper";

function clsx(...s: Array<string | false | undefined | null>) {
  return s.filter(Boolean).join(" ");
}

function useDebouncedValue<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function useInfiniteSentinel(opts: { enabled: boolean; onLoadMore: () => void; root?: Element | null }) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!opts.enabled) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) opts.onLoadMore();
      },
      { root: opts.root ?? null, rootMargin: "420px", threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [opts.enabled, opts.onLoadMore, opts.root]);

  return sentinelRef;
}

type CursorMeta = {
  per_page?: number;
  next_cursor: string | null;
  prev_cursor?: string | null;
  has_more: boolean;
};

type CursorResponse<T> = {
  data: T[];
  meta: CursorMeta;
  anchor_id?: number | null;
};

type ApiListResponse<T> = { data: T[] };

type ModeOption = { id: number; name: string };

type LoanProductRow = {
  id: number;
  name: string;
  mode: number;
  mode_name?: string;
  interest_rate: number;
  insuranceFeeRate: number;
  admin_fee_rate: number;
  processingFeeRate: number;
  installment: number;
  gracePeriod: number;
  joinFee: number;
  min_int_days: number;
  minAmount: number;
  maxAmount: number;
};

type ProductForm = {
  name: string;
  mode: string | number;
  interest_rate: string;
  min_int_days: string;
  installment: string;
  insuranceFeeRate: string;
  admin_fee_rate: string;
  processingFeeRate: string;
  minAmount: string;
  maxAmount: string;
  gracePeriod: string;
  joinFee: string;
};

function singularizeMode(name?: string) {
  if (!name) return "";
  return name.endsWith("s") ? name.slice(0, -1) : name;
}

/** ✅ Enterprise Fees UI (desktop + mobile) */
function FeesBlock({ p, rtl }: { p: LoanProductRow; rtl: boolean }) {
  const items = [
    { label: "Interest", value: `${p.interest_rate}%`, icon: Percent },
    { label: "Insurance", value: `${p.insuranceFeeRate}%`, icon: ShieldCheck },
    { label: "Admin", value: `${p.admin_fee_rate}%`, icon: Settings2 },
    { label: "Processing", value: `${p.processingFeeRate}%`, icon: Percent },
    { label: "Join Amt", value: helper.formatMoneyKES(p.joinFee), icon: BadgeDollarSign },
  ];

  return (
    <div className="rounded-xl border border-border bg-muted/30 p-2">
      <div className="hidden md:grid grid-cols-2 gap-x-4 gap-y-1 text-xs leading-tight">
        {items.map((it) => (
          <React.Fragment key={it.label}>
            <div className="text-muted-foreground inline-flex items-center gap-1">
              <it.icon className="h-3.5 w-3.5" />
              {it.label}
            </div>
            <div className={clsx("font-semibold", rtl ? "text-left" : "text-right")}>{it.value}</div>
          </React.Fragment>
        ))}
      </div>

      <div className="md:hidden flex flex-wrap gap-2">
        {items.map((it) => (
          <span
            key={it.label}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-1 text-xs"
          >
            <it.icon className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">{it.label}:</span>
            <span className="font-semibold">{it.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** ✅ Reusable Create/Edit form (no footer buttons inside) */
function LoanProductForm({
  form,
  setForm,
  modes,
  loading,
  onSubmit,
  submitLabel,
}: {
  form: ProductForm;
  setForm: React.Dispatch<React.SetStateAction<ProductForm>>;
  modes: ModeOption[];
  loading?: boolean;
  onSubmit: () => void;
  submitLabel: string;
}) {
  const selectedModeName = useMemo(() => {
    const m = modes.find((x) => String(x.id) === String(form.mode));
    return m?.name ?? "";
  }, [modes, form.mode]);

  const modeLabel = selectedModeName ? `per ${singularizeMode(selectedModeName)}` : "";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      {/* ✅ mobile-first layout: 1 col, desktop: 3 cols */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Product Name</label>
            <Input className="rounded-xl" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Mode</label>
            <select
              className="h-10 rounded-xl border border-border bg-background px-3 text-sm w-full"
              value={String(form.mode ?? "")}
              onChange={(e) => setForm((s) => ({ ...s, mode: e.target.value }))}
            >
              <option value="">— Select Mode —</option>
              {modes.map((m) => (
                <option key={m.id} value={String(m.id)}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Interest Rate {modeLabel ? <span className="text-muted-foreground">({modeLabel})</span> : null}
            </label>
            <Input
              className="rounded-xl"
              value={form.interest_rate}
              onChange={(e) => setForm((s) => ({ ...s, interest_rate: e.target.value }))}
              placeholder="Interest Rate"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Installment</label>
            <Input
              className="rounded-xl"
              type="number"
              value={form.installment}
              onChange={(e) => setForm((s) => ({ ...s, installment: e.target.value }))}
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Interest Fee Minimum Days</label>
            <Input className="rounded-xl" value={form.min_int_days} onChange={(e) => setForm((s) => ({ ...s, min_int_days: e.target.value }))} />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Insurance Rate {modeLabel ? <span className="text-muted-foreground">({modeLabel})</span> : null}
            </label>
            <Input className="rounded-xl" value={form.insuranceFeeRate} onChange={(e) => setForm((s) => ({ ...s, insuranceFeeRate: e.target.value }))} />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Admin Fee Rate {modeLabel ? <span className="text-muted-foreground">({modeLabel})</span> : null}
            </label>
            <Input className="rounded-xl" value={form.admin_fee_rate} onChange={(e) => setForm((s) => ({ ...s, admin_fee_rate: e.target.value }))} />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Processing Rate (% Amount Applied)</label>
            <Input className="rounded-xl" value={form.processingFeeRate} onChange={(e) => setForm((s) => ({ ...s, processingFeeRate: e.target.value }))} />
          </div>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Min Amount</label>
            <Input className="rounded-xl" type="number" value={form.minAmount} onChange={(e) => setForm((s) => ({ ...s, minAmount: e.target.value }))} />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Max Amount</label>
            <Input className="rounded-xl" type="number" value={form.maxAmount} onChange={(e) => setForm((s) => ({ ...s, maxAmount: e.target.value }))} />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">No. of Days before Repayment</label>
            <Input className="rounded-xl" type="number" value={form.gracePeriod} onChange={(e) => setForm((s) => ({ ...s, gracePeriod: e.target.value }))} />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">(Registration Fees)</label>
            <Input className="rounded-xl" type="number" value={form.joinFee} onChange={(e) => setForm((s) => ({ ...s, joinFee: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* ✅ Desktop-only submit button (mobile uses sticky footer in dialog) */}
      <div className="hidden md:flex justify-end">
        <Button type="submit" className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!!loading}>
          {loading ? "Saving..." : submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** ✅ Mobile-friendly DialogContent wrapper:
 * - full screen on mobile
 * - scrollable body
 * - sticky action footer (Cancel / Save)
 */
function ResponsiveDialogContent({
  title,
  open,
  onOpenChange,
  children,
  footer,
}: {
  title: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
          p-0 overflow-hidden
          w-[calc(100vw-1.5rem)] max-w-[720px]
          sm:rounded-2xl rounded-2xl
          md:p-0
          max-h-[92vh]
          md:max-h-[85vh]
          md:w-full
          sm:max-w-[720px]
          md:rounded-2xl
          [data-state=open]:animate-in [data-state=closed]:animate-out
          md:[&>button]:top-4 md:[&>button]:right-4
          [&>button]:hidden
        "
      >
        {/* ✅ Mobile header (sticky) */}
        <div className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-base font-semibold truncate">{title}</div>
            <div className="text-xs text-muted-foreground">Fill details then save</div>
          </div>

          <Button variant="ghost" className="h-9 w-9 rounded-xl" onClick={() => onOpenChange(false)} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* ✅ Scroll area */}
        <div className="px-4 py-4 overflow-y-auto max-h-[calc(92vh-3.25rem-4rem)] md:max-h-[calc(85vh-3.25rem-4rem)]">
          {children}
        </div>

        {/* ✅ Sticky footer actions (best for mobile) */}
        <div className="sticky bottom-0 z-20 border-t border-border bg-background/95 backdrop-blur px-4 py-3">
          {footer}
        </div>
      </DialogContent>
    </Dialog>
  );
}

type DialogMode = "create" | "edit";

export default function LoanProductsPage() {
  const qc = useQueryClient();
  const { t } = useTranslation();

  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const rtl = dir === "rtl";

  const mainEl = useMemo(() => document.querySelector("main"), []);
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);

  const [perPage, setPerPage] = useState(25);

  // ✅ Single reusable dialog for Create + Edit
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>("create");
  const [editingId, setEditingId] = useState<number | null>(null);

  const emptyForm: ProductForm = {
    name: "",
    mode: "",
    interest_rate: "",
    min_int_days: "",
    installment: "",
    insuranceFeeRate: "",
    admin_fee_rate: "",
    processingFeeRate: "",
    minAmount: "",
    maxAmount: "",
    gracePeriod: "",
    joinFee: "",
  };

  const [form, setForm] = useState<ProductForm>(emptyForm);

  const modesQ = useQuery({
    queryKey: ["loan_product_modes"],
    queryFn: async () => {
      const res = await apiFetch<ApiListResponse<ModeOption>>("/settings/loan-products/modes");
      return res.data;
    },
    staleTime: 60_000,
    retry: 1,
  });

  const productsInf = useInfiniteQuery({
    queryKey: ["loan_products_cursor", qDebounced, perPage],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("per_page", String(perPage));
      if (pageParam) params.set("cursor", String(pageParam));
      if (qDebounced?.trim()) params.set("q", qDebounced.trim());

      return apiFetch<CursorResponse<LoanProductRow>>(`/settings/loan-products?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last?.meta?.has_more ? last.meta.next_cursor : null),
    staleTime: 10_000,
    refetchOnWindowFocus: false,
  });

  const pages = productsInf.data?.pages ?? [];
  const products = useMemo(() => pages.flatMap((p) => p.data || []), [pages]);
  const lastMeta = pages.length ? pages[pages.length - 1]?.meta : undefined;

  const loadingFirst = productsInf.isLoading;
  const loadingMore = productsInf.isFetchingNextPage;

  const sentinelRef = useInfiniteSentinel({
    enabled: !!productsInf.hasNextPage && !productsInf.isFetchingNextPage && !productsInf.isLoading,
    onLoadMore: () => {
      if (productsInf.hasNextPage && !productsInf.isFetchingNextPage) productsInf.fetchNextPage();
    },
    root: (mainEl as Element) ?? null,
  });

  const validate = () => {
    if (!form.name.trim()) return "Product name is required";
    if (!form.mode) return "Mode is required";
    return null;
  };

  // ✅ Create
  const createMut = useMutation({
    mutationFn: async (payload: any) => apiFetch("/settings/loan-products", { method: "POST", body: payload }),
    onSuccess: async () => {
      toast.success("Loan product created");
      setDialogOpen(false);
      setDialogMode("create");
      setEditingId(null);
      setForm(emptyForm);
      await qc.invalidateQueries({ queryKey: ["loan_products_cursor"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to create product"),
  });

  // ✅ Edit
  const editMut = useMutation({
    mutationFn: async (payload: any) =>
      apiFetch(`/settings/loan-products/${payload.id}`, {
        method: "PUT",
        body: payload.data,
      }),
    onSuccess: async () => {
      toast.success("Loan product updated");
      setDialogOpen(false);
      setDialogMode("create");
      setEditingId(null);
      setForm(emptyForm);
      await qc.invalidateQueries({ queryKey: ["loan_products_cursor"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update product"),
  });

  const saving = createMut.isPending || editMut.isPending;

  const buildPayload = () => ({
    name: form.name.trim(),
    mode: form.mode,
    interest_rate: form.interest_rate,
    min_int_days: form.min_int_days,
    installment: form.installment,
    insuranceFeeRate: form.insuranceFeeRate,
    admin_fee_rate: form.admin_fee_rate,
    processingFeeRate: form.processingFeeRate,
    minAmount: form.minAmount,
    maxAmount: form.maxAmount,
    gracePeriod: form.gracePeriod,
    joinFee: form.joinFee,
  });

  const openCreate = () => {
    setDialogMode("create");
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p: LoanProductRow) => {
    setDialogMode("edit");
    setEditingId(p.id);
    setForm({
      name: p.name ?? "",
      mode: String(p.mode ?? ""),
      interest_rate: String(p.interest_rate ?? ""),
      min_int_days: String(p.min_int_days ?? ""),
      installment: String(p.installment ?? ""),
      insuranceFeeRate: String(p.insuranceFeeRate ?? ""),
      admin_fee_rate: String(p.admin_fee_rate ?? ""),
      processingFeeRate: String(p.processingFeeRate ?? ""),
      minAmount: String(p.minAmount ?? ""),
      maxAmount: String(p.maxAmount ?? ""),
      gracePeriod: String(p.gracePeriod ?? ""),
      joinFee: String(p.joinFee ?? ""),
    });
    setDialogOpen(true);
  };

  const submitDialog = () => {
    const err = validate();
    if (err) return toast.error(err);

    const payload = buildPayload();

    if (dialogMode === "create") {
      createMut.mutate(payload);
    } else {
      if (!editingId) return toast.error("Missing id");
      editMut.mutate({ id: editingId, data: payload });
    }
  };

  const dialogTitle = dialogMode === "create" ? "Create Loan Product" : "Edit Loan Product";

  return (
    <div dir={dir} className="space-y-6">
      <header className="border-b border-border bg-card sticky top-0 z-30 mb-3">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <HandCoins className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">Loan Products</h1>
              <p className="text-xs text-muted-foreground">Infinite scroll</p>
            </div>
          </div>

          <Button
            onClick={openCreate}
            className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl shadow-[0_10px_30px_rgba(124,58,237,0.18)]"
            disabled={loadingFirst}
          >
            <Plus className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
            Create
          </Button>
        </div>
      </header>

      {productsInf.isError ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-red-600">
            {(productsInf.error as any)?.message || "Failed to load products"}
          </CardContent>
        </Card>
      ) : null}

      <SettingsMenu />

      <Card className="shadow-card overflow-hidden">
        <CardHeader className="border-b border-border/60">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-lg">All Products</CardTitle>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className={clsx("absolute top-2.5 h-4 w-4 text-muted-foreground", rtl ? "right-3" : "left-3")} />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search products..."
                  className={clsx("w-full sm:w-72 rounded-xl", rtl ? "pr-10" : "pl-10")}
                />
              </div>

              <select
                className="h-10 rounded-xl border border-border bg-background px-3 text-sm"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    Per page: {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* ✅ Desktop table */}
          <div className="hidden md:block w-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
                <tr className="text-left">
                  <th className="p-3 whitespace-nowrap">Product</th>
                  <th className="p-3 whitespace-nowrap">Fees</th>
                  <th className="p-3 whitespace-nowrap">Installment</th>
                  <th className="p-3 whitespace-nowrap">Mode</th>
                  <th className="p-3 whitespace-nowrap">Days B4 Repayment</th>
                  <th className={clsx("p-3 whitespace-nowrap", rtl ? "text-left" : "text-right")}>Action</th>
                </tr>
              </thead>

              <tbody>
                {loadingFirst ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="border-b">
                      <td className="p-3">
                        <div className="h-4 w-48 rounded bg-muted animate-pulse" />
                      </td>
                      <td className="p-3">
                        <div className="h-16 w-80 rounded bg-muted animate-pulse" />
                      </td>
                      {Array.from({ length: 3 }).map((__, k) => (
                        <td key={k} className="p-3">
                          <div className="h-4 w-28 rounded bg-muted animate-pulse" />
                        </td>
                      ))}
                      <td className={clsx("p-3", rtl ? "text-left" : "text-right")}>
                        <div className="inline-block h-8 w-10 rounded bg-muted animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-10 text-center text-muted-foreground">
                      No data available
                    </td>
                  </tr>
                ) : (
                  products.map((p) => (
                    <tr key={p.id} className="border-b hover:bg-muted/20">
                      <td className="p-3">
                        <div className="font-semibold">{p.name}</div>
                        <div className="mt-1 flex flex-wrap gap-2">
                          <Badge variant="secondary" className="rounded-full">
                            Min: {helper.formatMoneyKES(p.minAmount)}
                          </Badge>
                          <Badge variant="secondary" className="rounded-full">
                            Max: {helper.formatMoneyKES(p.maxAmount)}
                          </Badge>
                          <Badge variant="secondary" className="rounded-full">
                            Min int days: {p.min_int_days}
                          </Badge>
                        </div>
                      </td>

                      <td className="p-3">
                        <FeesBlock p={p} rtl={rtl} />
                      </td>

                      <td className="p-3">{p.installment}</td>
                      <td className="p-3">{p.mode_name ?? "—"}</td>
                      <td className="p-3">{p.gracePeriod}</td>

                      <td className={clsx("p-3", rtl ? "text-left" : "text-right")}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="rounded-xl">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent align={rtl ? "start" : "end"} className="rounded-xl">
                            <DropdownMenuItem onClick={() => openEdit(p)}>
                              <Pencil className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                              Edit
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}

                {loadingMore ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`more-${i}`} className="border-b">
                      <td className="p-3">
                        <div className="h-4 w-48 rounded bg-muted animate-pulse" />
                      </td>
                      <td className="p-3">
                        <div className="h-16 w-80 rounded bg-muted animate-pulse" />
                      </td>
                      {Array.from({ length: 3 }).map((__, k) => (
                        <td key={k} className="p-3">
                          <div className="h-4 w-28 rounded bg-muted animate-pulse" />
                        </td>
                      ))}
                      <td className={clsx("p-3", rtl ? "text-left" : "text-right")}>
                        <div className="inline-block h-8 w-10 rounded bg-muted animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : null}
              </tbody>
            </table>
          </div>

          {/* ✅ Mobile cards */}
          <div className="md:hidden p-3 space-y-3">
            {loadingFirst ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border bg-background p-4">
                  <div className="h-4 w-44 bg-muted animate-pulse rounded" />
                  <div className="mt-3 h-16 w-full bg-muted animate-pulse rounded" />
                  <div className="mt-3 h-4 w-32 bg-muted animate-pulse rounded" />
                </div>
              ))
            ) : products.length === 0 ? (
              <div className="rounded-xl border border-border p-6 text-center text-sm text-muted-foreground">No data available</div>
            ) : (
              products.map((p) => (
                <Card key={p.id} className="shadow-card rounded-2xl overflow-hidden">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className="mt-1 flex flex-wrap gap-2">
                          <Badge variant="secondary" className="rounded-full">
                            {p.mode_name ?? "—"}
                          </Badge>
                          <Badge variant="secondary" className="rounded-full">
                            Install: {p.installment}
                          </Badge>
                          <Badge variant="secondary" className="rounded-full">
                            Repay in: {p.gracePeriod}d
                          </Badge>
                        </div>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="rounded-xl">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align={rtl ? "start" : "end"} className="rounded-xl">
                          <DropdownMenuItem onClick={() => openEdit(p)}>
                            <Pencil className={clsx("h-4 w-4", rtl ? "ml-2" : "mr-2")} />
                            Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="rounded-full">
                        Min: {helper.formatMoneyKES(p.minAmount)}
                      </Badge>
                      <Badge variant="outline" className="rounded-full">
                        Max: {helper.formatMoneyKES(p.maxAmount)}
                      </Badge>
                      <Badge variant="outline" className="rounded-full">
                        Min int days: {p.min_int_days}
                      </Badge>
                    </div>

                    <FeesBlock p={p} rtl={rtl} />
                  </CardContent>
                </Card>
              ))
            )}

            <div className="pt-2">
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => productsInf.fetchNextPage()}
                disabled={!productsInf.hasNextPage || productsInf.isFetchingNextPage}
              >
                {productsInf.isFetchingNextPage ? "Loading..." : productsInf.hasNextPage ? "Load more" : "No more"}
              </Button>
            </div>
          </div>

          <div ref={sentinelRef} />

          <div className="flex items-center justify-between gap-3 border-t border-border/60 p-4">
            <div className="text-sm text-muted-foreground">
              Loaded <span className="font-medium text-foreground">{products.length}</span>{" "}
              {lastMeta?.has_more ? " (more available)" : " (end)"}
            </div>

            <Button
              variant="outline"
              className="rounded-xl hidden md:inline-flex"
              onClick={() => productsInf.fetchNextPage()}
              disabled={!productsInf.hasNextPage || productsInf.isFetchingNextPage}
            >
              {productsInf.isFetchingNextPage ? "Loading..." : productsInf.hasNextPage ? "Load more" : "No more"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ✅ REUSABLE CREATE/EDIT DIALOG (mobile fixed: full-screen + scroll + sticky footer) */}
      <ResponsiveDialogContent
        title={dialogTitle}
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) {
            setDialogMode("create");
            setEditingId(null);
            setForm(emptyForm);
          }
        }}
        footer={
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setDialogOpen(false)} disabled={saving}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitDialog} disabled={saving}>
              {saving ? (
                <>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    Saving...
                  </span>
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {dialogMode === "create" ? "Create" : "Save"}
                </>
              )}
            </Button>
          </div>
        }
      >
        <LoanProductForm
          form={form}
          setForm={setForm}
          modes={modesQ.data ?? []}
          loading={saving}
          onSubmit={submitDialog}
          submitLabel={dialogMode === "create" ? "Create" : "Save"}
        />
      </ResponsiveDialogContent>
    </div>
  );
}